import { apiFetch } from "./client";

/**
 * D-048 — On-Demand Manager-Scoped BI Sync (Phase 1 email + Phase 2 GUID).
 * Status object = CManagerScopedSyncRun (MP3) — wizard/drawer читають ЙОГО,
 * не ad-hoc toast. Усі гейти backend-owned (D-017): allowedActions.canRun +
 * reason приходять з сервера, UI лише рендерить.
 *
 * Phase 2: resolvable_key = User.cCBiManagerKey (manager_id_hex GUID) задано —
 * matching-приорітет #1; runner іде через BI ?manager_ids=. Каталог менеджерів
 * для picker'а сервиться з кешу (runner його оновлює) — 0 live BI-викликів.
 */

export type ManagerSyncMappingStatus =
  | "resolvable_key"
  | "resolvable_email"
  | "ambiguous_email"
  | "no_email";
export type ManagerSyncRunState = "queued" | "running" | "success" | "empty" | "error";

/** Sync може запускатись для цього mapping-стану (дзеркало MappingResolver::isResolvable). */
export function isResolvableMapping(status: ManagerSyncMappingStatus): boolean {
  return status === "resolvable_key" || status === "resolvable_email";
}

export interface ManagerSyncMapping {
  status: ManagerSyncMappingStatus;
  email: string | null;
  reason: string | null;
  biManagerKey: string | null;
  biManagerKeySource: "manual" | "sync" | null;
}

export interface ManagerSyncLastRun {
  id: string;
  status: ManagerSyncRunState;
  triggerType: "auto" | "manual";
  /** Phase 2: яким шляхом ішов прогін — email (Phase 1) | guid (manager_ids=). */
  managerKeyType: "email" | "guid";
  foundCount: number;
  createdCount: number;
  updatedCount: number;
  errorMessage: string | null;
  startedAt: string | null;
  finishedAt: string | null;
  createdAt: string | null;
}

export interface ManagerSyncStatus {
  userId: string;
  email: string | null;
  mapping: ManagerSyncMapping;
  lastRun: ManagerSyncLastRun | null;
  /** MP6: live — скільки акаунтів менеджер реально бачить після ACL/archived. */
  visibleAfterAclCount: number;
  flagEnabled: boolean;
  /** Phase 2: кеш каталогу присутній → picker доступний. */
  catalogAvailable: boolean;
  allowedActions: { canRun: boolean; reason: string | null };
}

/** Один менеджер з BI-каталогу (crm_managers_v1, ключ = manager_id_hex). */
export interface ManagerCatalogEntry {
  managerIdHex: string;
  name: string | null;
  code1c: string | null;
  email: string | null;
  isAgent: boolean | null;
  isActive: boolean;
}

export interface ManagerCatalogPayload {
  available: boolean;
  stale: boolean;
  fetchedAt: string | null;
  snapshotAt: string | null;
  schemaVersion: string | null;
  items: ManagerCatalogEntry[];
}

export function fetchManagerCatalog(signal?: AbortSignal): Promise<ManagerCatalogPayload> {
  return apiFetch<ManagerCatalogPayload>("/Custom/manager-sync/catalog", { signal });
}

export function fetchManagerSyncStatus(
  userId: string,
  signal?: AbortSignal,
): Promise<ManagerSyncStatus> {
  return apiFetch<ManagerSyncStatus>(
    `/Custom/manager-sync/users/${encodeURIComponent(userId)}/status`,
    { signal },
  );
}

export function runManagerSync(
  userId: string,
): Promise<{ status: string; runId: string; managerKey: string }> {
  return apiFetch(`/Custom/manager-sync/users/${encodeURIComponent(userId)}/run`, {
    method: "POST",
    body: {},
  });
}

export function setBiManagerKey(
  userId: string,
  biManagerKey: string | null,
): Promise<{ status: string; biManagerKey: string | null }> {
  return apiFetch(`/Custom/admin-center/users/${encodeURIComponent(userId)}/bi-manager-key`, {
    method: "POST",
    body: { biManagerKey: biManagerKey ?? "" },
  });
}
